import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { MouseEvent } from 'react'
import { CLICK_INTENT_WAIT_MS } from '@/styling/tokens.interaction'

// The one place the app reads a click's count (`event.detail`); lint refuses it elsewhere.

/** A click from Enter, Space, or assistive technology, which has no click count. */
export function isKeyboardClick(event: { detail: number }) {
  return event.detail === 0
}

/** The second or later press of a multi-click, such as a double-click's mousedown. */
export function isRepeatClick(event: { detail: number }) {
  return event.detail > 1
}

export interface ClickIntentHandlers<T extends Element> {
  onClick: (event: MouseEvent<T>) => void
  onDoubleClick: (event: MouseEvent<T>) => void
  /** Drops a waiting single click, as when part of the target acts at once. */
  cancel: () => void
}

/**
 * Tells a single click from a double-click on one target. A mouse click waits
 * `CLICK_INTENT_WAIT_MS` for a second; a keyboard click runs at once. A second
 * click after `onClick` already ran does nothing unless the single click
 * returned an undo. Reversible actions can then honor the browser's own
 * double-click interval, including slower accessibility settings.
 */
export function useClickIntent<T extends Element = Element>({
  onClick,
  onDoubleClick,
  waitMs = CLICK_INTENT_WAIT_MS,
}: {
  /** An optional undo lets a native, slower double-click take precedence. */
  onClick: () => void | (() => void)
  onDoubleClick: (event: MouseEvent<T>) => void
  waitMs?: number
}): ClickIntentHandlers<T> {
  const latest = useRef({ onClick, onDoubleClick })
  latest.current = { onClick, onDoubleClick }
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Whether this click sequence's single click already ran.
  const singleRan = useRef(false)
  const undoSingle = useRef<(() => void) | null>(null)

  const cancel = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current)
    timer.current = null
  }, [])

  useEffect(() => cancel, [cancel])

  return useMemo(
    () => ({
      cancel,
      onClick: (event: MouseEvent<T>) => {
        cancel()
        if (isKeyboardClick(event)) {
          singleRan.current = true
          latest.current.onClick()
          return
        }
        // The second click of a double-click: onDoubleClick follows.
        if (isRepeatClick(event)) return
        singleRan.current = false
        undoSingle.current = null
        timer.current = setTimeout(() => {
          timer.current = null
          singleRan.current = true
          undoSingle.current = latest.current.onClick() ?? null
        }, waitMs)
      },
      onDoubleClick: (event: MouseEvent<T>) => {
        cancel()
        if (singleRan.current) {
          if (!undoSingle.current) return
          undoSingle.current()
          undoSingle.current = null
        }
        latest.current.onDoubleClick(event)
      },
    }),
    [cancel, waitMs]
  )
}
